// Client for the BKA discovery indexer (the Sprint 7 worker). Browser-
// direct — the worker is CORS-open by design. Authenticated routes take
// the creator's Google ID token (the raw JWT we already hold in
// authStore); the worker verifies it. We never send GitHub/CF tokens
// here — discovery only ever needs the public Google identity.

const INDEXER = "https://bka-indexer.chrisbender999.workers.dev";

export interface IndexerItem {
  title: string;
  summary: string;
  tags: string[];
  kinds: string[];
  publishedAt: string;
  url: string;
  thumbnailUrl: string | null;
  creator: { handle: string; siteUrl: string };
}

export interface CreatorStats {
  creator: { handle: string; displayName: string; bio: string; siteUrl: string };
  items: IndexerItem[];
}

async function call(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${INDEXER}${path}`, init);
}

/** Register the creator's feed for discovery. Returns nothing on success,
 *  throws with the worker's message on failure (e.g. unreachable feed,
 *  expired token). */
export async function registerDiscovery(
  googleIdToken: string, handle: string, feedUrl: string, siteUrl: string,
): Promise<void> {
  const res = await call("/api/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ googleIdToken, handle, feedUrl, siteUrl }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(j.error || `register failed (${res.status})`);
  }
}

export async function unregisterDiscovery(googleIdToken: string): Promise<void> {
  const res = await call("/api/unregister", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ googleIdToken }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(j.error || `unregister failed (${res.status})`);
  }
}

/** Priority-pull hint after a publish. Best-effort — never throws (a
 *  failed hint just means the indexer picks the update up on its normal
 *  cadence). */
export async function pingRefresh(googleIdToken: string): Promise<void> {
  try {
    await call("/api/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ googleIdToken }),
    });
  } catch { /* non-blocking */ }
}

/** Read a creator's indexed profile + items. Returns null if the creator
 *  isn't registered (404). */
export async function getCreatorStats(handle: string): Promise<CreatorStats | null> {
  const res = await call(`/api/creators/${encodeURIComponent(handle)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`indexer ${res.status}`);
  return res.json() as Promise<CreatorStats>;
}
